// src/i18n/I18nContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { translations, LANGUAGES, getDefaultLanguage } from './translations';

const I18nContext = createContext();

const STORAGE_KEY = 'govsky_language';

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Get saved language or default to English
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && LANGUAGES[saved] ? saved : 'en';
  });

  // Save language preference
  const setLanguage = useCallback((lang) => {
    if (LANGUAGES[lang]) {
      setLanguageState(lang);
      localStorage.setItem(STORAGE_KEY, lang);
      // Update document language for accessibility
      document.documentElement.lang = lang;
    }
  }, []);

  // Set initial document language
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Get translation by key path (e.g., 'header.login')
  const t = useCallback((key, fallback) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    
    // Fallback to English if not found
    if (value === undefined) {
      value = translations.en;
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }
    }
    
    return value ?? fallback ?? key;
  }, [language]);

  // Get all available languages
  const availableLanguages = useMemo(() => Object.values(LANGUAGES), []);

  // Get current language info
  const currentLanguage = useMemo(() => LANGUAGES[language], [language]);

  // Set language based on country (for onboarding)
  const setLanguageFromCountry = useCallback((countryCode) => {
    const defaultLang = getDefaultLanguage(countryCode);
    setLanguage(defaultLang);
  }, [setLanguage]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    availableLanguages,
    currentLanguage,
    setLanguageFromCountry,
    LANGUAGES,
  }), [language, setLanguage, t, availableLanguages, currentLanguage, setLanguageFromCountry]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export default I18nContext;

